import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ButtonLoader } from '@/components/button-loader';
import { useApplications } from '@/store/application';
// import { useUsers } from '@/store/user';

import { PageLayout } from '@/components/page-layout';
import { useLayout } from '@/contexts/layout-context';
import { ImageIcon } from 'lucide-react';

export const CreateApplicationPage = () => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [baseUrl, setBaseUrl] = useState('');
    const [docsUrl, setDocsUrl] = useState('');
    const [callbackUrl, setCallbackUrl] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [isEnabled, setIsEnabled] = useState(true);
    const createApplication = useApplications((state) => state.create);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();

    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await createApplication({
                name,
                description,
                logo_url: logoUrl || undefined,
                base_url: baseUrl || undefined,
                docs_url: docsUrl || undefined,
                callback_url: callbackUrl || undefined,
                is_public: isPublic,
                is_enabled: isEnabled,
                status: isEnabled ? 1 : 0,
            });
            toast.success('Application created successfully');
            navigate('/m/applications');
        } catch (err) {
            console.error(err);
            toast.error('Failed to create application. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        setBreadcrumbs([{ name: 'Applications', to: '/m/applications' }, { name: 'Create' }]);
    }, [setBreadcrumbs]);

    return (
        <PageLayout title="Create Application" description="Register a new client application.">
            <div className="bg-white dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden p-6 md:p-8">

                <form onSubmit={submit} className="space-y-6">
                    <div className="grid gap-6 md:grid-cols-2">
                        {/* Row 1: Name and Base URL */}
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Name</label>
                            <input
                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                placeholder="e.g. Frontend App"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Base URL</label>
                            <input
                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                placeholder="https://app.example.com"
                                value={baseUrl}
                                onChange={(e) => setBaseUrl(e.target.value)}
                            />
                        </div>

                        {/* Row 2: Description */}
                        <div className="space-y-2 md:col-span-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Description</label>
                            <textarea
                                rows={2}
                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full resize-none"
                                placeholder="Main customer facing application"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>

                        {/* Row 3: Documentation URL & Callback URL */}
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Documentation URL</label>
                            <input
                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                placeholder="https://docs.example.com"
                                value={docsUrl}
                                onChange={(e) => setDocsUrl(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Callback URL</label>
                            <input
                                className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                placeholder="https://app.example.com/callback"
                                value={callbackUrl}
                                onChange={(e) => setCallbackUrl(e.target.value)}
                            />
                        </div>

                        {/* Row 4: Logo Upload & Toggles */}
                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Logo</label>
                            <div className="flex gap-4">
                                {logoUrl ? (
                                    <div className="h-20 w-20 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-center overflow-hidden shrink-0">
                                        <img src={logoUrl} alt="Logo preview" className="w-full h-full object-cover" />
                                    </div>
                                ) : (
                                    <div className="h-20 w-20 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex flex-col items-center justify-center shrink-0 text-slate-400">
                                        <ImageIcon className="w-6 h-6 mb-1 opacity-50" />
                                        <span className="text-[10px] text-center leading-tight">No logo</span>
                                    </div>
                                )}
                                <div className="flex-1 flex flex-col justify-center gap-2">
                                    <input
                                        className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white placeholder:text-slate-400 w-full"
                                        placeholder="Logo URL..."
                                        value={logoUrl}
                                        onChange={(e) => setLogoUrl(e.target.value)}
                                    />
                                    <div className="flex justify-end">
                                        <label className="cursor-pointer bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-1.5 flex items-center text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors shrink-0">
                                            Upload File
                                            <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6 pt-6 pl-0 md:pl-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Is Public</label>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Allow public registration and access.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" className="sr-only peer" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-orange-500"></div>
                                </label>
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Is Enabled</label>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Enable or disable this application immediately.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" className="sr-only peer" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-orange-500"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                        <Link
                            to="/m/applications"
                            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center min-w-40 gap-2"
                        >
                            {isSubmitting ? <ButtonLoader /> : 'Create Application'}
                        </button>
                    </div>
                </form>
            </div>
        </PageLayout>
    );
};
